import { useCallback, useEffect, useRef, useState } from "react";

interface ReplayState {
  /** 0 → 100 as the price line draws itself left to right. */
  progress: number;
  /** Jump straight to the finished state (used while the user drags). */
  skip: () => void;
}

/**
 * Drives the "draw the price line" animation. Bump `runId` to replay from zero.
 * Respects prefers-reduced-motion by snapping to the finished frame.
 */
export function useReplay(runId: number, duration = 1900): ReplayState {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(0);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      setProgress(100);
      return undefined;
    }

    setProgress(0);
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const ratio = Math.min(1, (timestamp - start) / duration);
      if (ratio >= 1) {
        setProgress(100);
        return;
      }
      setProgress(ratio * 100);
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [runId, duration]);

  const skip = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setProgress(100);
  }, []);

  return { progress, skip };
}